import { AxiosError, create } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AIScope, ChatAgentContextPayload, ChatAgentMessage, ChatAgentRequestPayload,
  ConversationSummary, FeedbackPayload, ImageContextFile } from "./AISupportChat.types";
import {
  AGENT_API_PREFIX, AGENT_BASE_URLS, ALLOWED_IMAGE_TYPES, MAX_IMAGE_DIMENSION, MAX_IMAGE_SIZE_BYTES
} from "./const";
import { buildConversationPreview, formatTimestampFromIso, readImageAsBase64, scrollToLastMessage } from "./utils";
import { useAuthStore, useLanguageStore } from "../../stores";

export const useApiAgentChatAxios = (
  sessionId: string, 
  updateConversationMeta?: (sid: string, patch: Partial<ConversationSummary>) => void,
  loadConversationsList?: () => Promise<void>,
  removeConversationFromList?: (sid: string) => void
) => {
  const { t } = useTranslation("");

  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);

  const sessionToken = useAuthStore(state => state.sessionToken);
  const accessToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const userData = useAuthStore(state => state.userData);
  const userMgmtData = useAuthStore(state => state.userMgmtData);
  const corporateId = (userData?.organization_id ?? userMgmtData?.id)?.toString() ?? "";
  const language = useLanguageStore(state => state.language?.id ?? state.languageCookie?.id) ?? "";

  const [messages, setMessages] = useState<ChatAgentMessage[]>([]);
  const [apiError, setApiError] = useState("");
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [blockUserInput, setBlockUserInput] = useState(false);
  const [isRetrievingHistory, setIsRetrievingHistory] = useState(false);
  const [listImgContext, setListImgContext] = useState<ImageContextFile[]>([]);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [contextImgError, setContextImgError] = useState("");
  const [isDeletingImage, setIsDeletingImage] = useState(false);
  const [isRetrievingImageContext, setIsRetrievingImageContext] = useState(false);

  const diagramLabel = t("previewDiagram") || "Diagram";
  const apiTimeout = 30000; //30 seconds
  
  const buildAgentUrl = (
    scope: AIScope
  ) => `${AGENT_BASE_URLS.SUPPORT}/${AGENT_API_PREFIX.SUPPORT}/${scope}`;

  const apiInstance = create({
    timeout: apiTimeout,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "x-ada-session-token": sessionToken ?? ""
    }
  });

  const checkEndpointURI = () => {
    if(!import.meta.env.VITE_AGENT_AI_SUPPORT) {
      setApiError(t("ai_support_chat_endpoint_error"));
      console.error("No endpoint specified for the agent");
      return false;
    }

    return true;
  };

  const sendMessage = async (message: string) => {

    if (!checkEndpointURI()) return;

    const endpoint = buildAgentUrl("message");

    const payload: ChatAgentRequestPayload = {
      session_id: sessionId,
      session_attributes: {
        corporate_id: corporateId,
        language
      },
      payload: {
        end_session: false,
        user_id: userData?.id_user.toString() ?? "",
        user_message: message
      }
    };

    const isFirstMessage = messages.length === 0;

    setBlockUserInput(true);
    setIsSendingMessage(true);

    apiInstance
      .post(endpoint, payload)
      .then(() => {
        if (isFirstMessage && updateConversationMeta) {
          const nowIso = new Date().toISOString();
          updateConversationMeta(sessionId, {
            created_at: nowIso,
            message_count: 1,
            title: message.slice(0, 60),
            updated_at: nowIso
          });
        }

        setMessages((prev)=>[
          ...prev,
          {
            sender: "user",
            end_session: false,
            first_message: prev.length === 0,
            response: message,
            timestamp: formatTimestampFromIso(new Date().toISOString())
          }
        ]);
        
      })
      .catch((error) => {
        console.error(
          "[ChatAgent] An error occurred sending message to agent",
          error
        );
        if (isFirstMessage && removeConversationFromList) {
          removeConversationFromList(sessionId);
        }
        if (
          error.response &&
          error.response.data &&
          error.response.data.response
        ) {
          setApiError(error.response.data.response as string);
        } else if (error.message) {
          setApiError(`${error.message} [${error.code}]`);
        }
      })
      .finally(() => {
        setIsSendingMessage(false);
        retrieveResponseFromAgent().finally(() => {
          if (isFirstMessage && loadConversationsList) loadConversationsList();
        });
      });
  };

  // get info preview for every chat session with the agent
  const fetchSessionInfo = async (
    sid: string,
    corporateId: string | number
  ): Promise<Partial<ConversationSummary>> => {
    const endpoint = buildAgentUrl("messages") + `/${sid}`;
    
    if (!endpoint) return {};
    try {
      const res = await apiInstance.get(endpoint, {
        params: { corporate_id: corporateId }
      });
      const items: Array<{ role?: string; text?: string; timestamp?: string }> =
          res.data?.messages ?? [];
      if (items.length === 0) {
        return { message_count: 0 };
      }
      const firstUser = items.find((m) => m.role === "user");
      const lastAssistant = [...items].reverse().find((m) => m.role !== "user");
      const first = items[0];
      const last = items[items.length - 1];
      return {
        message_count: items.length,
        title: firstUser?.text?.slice(0, 60),
        preview: buildConversationPreview(lastAssistant?.text, diagramLabel),
        created_at: first?.timestamp,
        updated_at: last?.timestamp
      };
    } catch (error) {
      const status = (error as AxiosError)?.response?.status;
      if (status === 404) {
        return { message_count: 0 };
      }
      console.error("[ChatAgent] Failed to fetch session messages", sid, error);
      return {};
    }
  };

  const getChatHistory = useCallback((sid: string) => {
    if (!sessionId || !corporateId) return;
    const endpoint = buildAgentUrl("messages") + `/${sid}`;
    if (!endpoint) return;
    setIsRetrievingHistory(true);

    apiInstance
      .get(endpoint, {
        params: { corporate_id: corporateId }
      })
      .then((res) => {
        const items = res.data?.messages ?? [];
        if (items.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const mapped: ChatAgentMessage[] = items.map((message: any) => ({
            sender: message.role === "user" ? "user" : "assistant",
            response: message.text,
            fist_message: false,
            end_session: false,
            from_history: true,
            timestamp: formatTimestampFromIso(message.timestamp)
          }));
          setMessages(mapped);
        
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const firstUser = items.find((m: any) => m.role === "user");
          const lastAssistant = [...items]
            .reverse()
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .find((m: any) => m.role !== "user");
          updateConversationMeta && updateConversationMeta(sessionId, {
            title: firstUser?.text?.slice(0, 60),
            preview: buildConversationPreview(
              lastAssistant?.text,
              t("previewDiagram") || "Diagram"
            ),
            message_count: items.length,
            updated_at:
                items[items.length - 1]?.timestamp ?? new Date().toISOString()
          });
          scrollToLastMessage(messagesContainerRef);
        } else {
          setMessages([]);
        }
      })
      .catch((error: AxiosError) => {
        if (error?.response?.status !== 404) {
          console.error(
            "[ChatAgent] An error occurred while retrieving agent chat history",
            error
          );
          setApiError(t("historyUnavailable") || "");
        }
        setMessages([]);
      })
      .finally(() => {
        setIsRetrievingHistory(false);
      });
  }, [
    apiInstance,
    t,
    sessionId,
    corporateId,
    updateConversationMeta
  ]);

  const submitFeedback = async (
    payload: FeedbackPayload, 
    feedbackTargetIndex: number | null, 
    messages:ChatAgentMessage[]
  ) => {
    if (
      !sessionId ||
      !corporateId ||
      feedbackTargetIndex === null
    ) {
      return;
    }

    const target = messages[feedbackTargetIndex];
    if (!target) return;
    const feedbackEndpoint = buildAgentUrl("feedback");
    if (!feedbackEndpoint) return;

    apiInstance
      .put(
        feedbackEndpoint,
        {
          session_id: sessionId,
          message_id: `msg_${feedbackTargetIndex}`,
          corporate_id: corporateId,
          feedback: payload.feedback,
          rating: payload.rating,
          message_preview: target.response?.slice(0, 200),
          comment: payload.comment
        }
      )
      .catch((error) => {
        console.error("[ChatAgent] Failed to submit feedback", error);
      });
  };

  const uploadImageContext = async (file: File): Promise<boolean> => {
    if (!checkEndpointURI()) return false;

    setContextImgError("");

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setContextImgError(t("error_occurred") || "An error occurred. Try again later.");
      return false;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setContextImgError(t("error_occurred") || "An error occurred. Try again later.");
      return false;
    }

    setIsUploadingImage(true);

    try {
      const { base64, width, height } = await readImageAsBase64(file);

      if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
        setContextImgError(t("error_occurred") || "An error occurred. Try again later.");
        return false;
      }

      const endpoint = buildAgentUrl("context");

      const payload: ChatAgentContextPayload = {
        image_base64: base64,
        media_type: file.type as ChatAgentContextPayload["media_type"],
        session_id: sessionId
      };

      await apiInstance.post(endpoint, payload);
      const fileWithId = Object.assign(file, { id: crypto.randomUUID() }) as ImageContextFile;
      setListImgContext([...listImgContext, fileWithId]);
      return true;
    } catch (error) {
      const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      setContextImgError(detail || t("error_occurred") || "An error occurred. Try again later.");
      return false;
    } finally {
      setIsUploadingImage(false);
    }
  };

  const deleteImageContext = async (index?: number): Promise<boolean> => {
    if (!checkEndpointURI()) return false;

    setContextImgError("");
    setIsDeletingImage(true);

    const endpoint = buildAgentUrl("context");

    try {
      await apiInstance.delete(endpoint, {
        params: {
          session_id: sessionId,
          ...(index !== undefined ? { index } : {})
        }
      });

      setListImgContext((prev) =>
        index !== undefined ? prev.filter((_, i) => i !== index) : []
      );
      return true;
    } catch (error) {
      const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      setContextImgError(detail || t("error_occurred") || "An error occurred. Try again later.");
      return false;
    } finally {
      setIsDeletingImage(false);
    }
  };

  const getImageContext = async (sid: string): Promise<boolean> => {
    if (!checkEndpointURI()) return false;

    setContextImgError("");
    setIsRetrievingImageContext(true);

    const endpoint = buildAgentUrl("context");

    try {
      const response = await apiInstance.get(endpoint, {
        params: { session_id: sid }
      });

      const contexts: Array<{
        structured?: { page?: string; description?: string };
      }> = response.data?.contexts ?? [];

      const mapped: ImageContextFile[] = contexts.map((ctx) => {
        const label = ctx.structured?.page
          || ctx.structured?.description?.slice(0, 40)
          || t("ai_support_chat_screenshot")
          || "screenshot";
        return Object.assign(new File([], label), { id: crypto.randomUUID() }) as ImageContextFile;
      });

      setListImgContext(mapped);
      return true;
    } catch (error) {
      const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      setContextImgError(detail || t("error_occurred") || "An error occurred. Try again later.");
      setListImgContext([]);
      return false;
    } finally {
      setIsRetrievingImageContext(false);
    }
  };

  const retrieveResponseFromAgent = async () => {
    if (!checkEndpointURI()) return;

    setMessages((prev)=>[
      ...prev,
      {
        sender: "assistant",
        end_session: false,
        first_message: false,
        response: ""
      }
    ]);

    const endpoint = buildAgentUrl("response"); 
    // use "/stream" for server sent events. read doc for major informations.
    try {
      const response = await apiInstance
        .get(endpoint, {
          params: {
            corporate_id: corporateId,
            session_id: sessionId
          }
        });

      if (!response) {
        console.error("[ChatAgent] Stream not available");
        return;
      }
      
      setMessages((prev) => {
        //update baloon loading message
        const updated = [...prev];
        updated[updated.length - 1] = {
          sender: "assistant",
          end_session: false,
          first_message: false,
          response: response.data.response
        };
        return updated;
      });

      scrollToLastMessage(messagesContainerRef);
      setBlockUserInput(false);

    } catch (error) {
      console.error(
        "[ChatAgent] An error has occurred while fetching agent's response:",
        error
      );
      setBlockUserInput(false);
    }
  };

  const initChat = () => {
    setApiError("");
    setBlockUserInput(false);
    setMessages([]);
  };

  useEffect(() => {
    if (!blockUserInput && messageInputRef.current)
      messageInputRef.current.focus();
  }, [blockUserInput]);

  return {
    apiError,
    apiInstance,
    blockUserInput,
    deleteImageContext,
    fetchSessionInfo,
    contextImgError,
    getImageContext,
    initChat,
    isDeletingImage,
    isRetrievingImageContext,
    isSendingMessage,
    isUploadingImage,
    messages,
    messagesContainerRef,
    messageInputRef,
    sendMessage,
    submitFeedback,
    getChatHistory,
    isRetrievingHistory,
    uploadImageContext,
    listImgContext
  };
};
